import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store/store";

import useAlerts from "../useAlerts";
import useLoading from "../useLoading";
import useApiErrorHandler from "../useApiErrorHandler";
import {
    setQuotationForm,
    updateQuotationField,
    clearQuotationForm,
    setLoading,
    setError,
    setQuotations,
    setPendingQuotations,
    setSelectedQuotation,
    openQuotationModal,
    closeQuotationModal,
    clearSelectedQuotation,
    setSelectedProduct,
    updateSelectedProductField,
    clearSelectedProduct,
    setQuotationHistory,
    // clearPendingQuotations,
    // clearQuotationHistory,
    clearQuotations,
    setActiveBranchId,
    openProcessModal,
    closeProcessModal,
    removePendingQuotationById,
    removeQuotationHistoryById,
    clearQuotationState,
    setFieldErrors,
    clearFieldErrors,
} from "../../store/slices/sale/quotationSlice";
import QuotationService from "../../services/sale/QuotationService";
import { QuotationFormValues } from "../../types/sale/quotation/quotation";
import useConfirmModal from "../useConfirmModal";
import { initializeQuotationMapper } from "../../mappers/sale/quotation/initializeQuotationMapper";
import { quotationDetailToFormMapper } from "../../mappers/sale/quotation/quotationDetailToFormMapper";
import { createQuotationMapper } from "../../mappers/sale/quotation/createQuotationMapper";
import { useNavigate } from "react-router-dom";
import useProductCatalog from "../productpanel/useProductCatalog";
import useContinueSale from './useContinueSale';


export const useQuotation = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { show: showAlert } = useAlerts();
    const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
    const { handle } = useApiErrorHandler();
    const confirm = useConfirmModal();

    const { resetProductPanelState } = useProductCatalog();
    const continueSale = useContinueSale();

    const {
        activeBranchId,
        form,
        quotations,
        pendingQuotations,
        quotationHistory,
        selectedQuotation,
        selectedProduct,
        loading,
        error,
        fieldErrors,
        filters,
        showQuotationModal,
        showProcessModal,
    } = useSelector(
        (state: RootState) => state.quotation
    );

    /**
     * 📋 Buscar cotizaciones
     */
    const search = async (payload: any) => {

        try {

            showLoadingIndicator();
            dispatch(setLoading(true));
            dispatch(setError(null));

            const response = await QuotationService.search(payload);

            const { statusCode, data } = response;

            if (statusCode === 200) {
                dispatch(setQuotations(data.content ?? []));
            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();
            dispatch(setLoading(false));

        }

    };

    /**
     * 🔍 Obtener detalle
     */
    const findById = async (quotationId: number) => {

        try {

            showLoadingIndicator();
            dispatch(setLoading(true));
            dispatch(setError(null));

            const response = await QuotationService.findById(quotationId);

            // console.log(response);

            const { statusCode, data } = response;

            if (statusCode === 200) {
                dispatch(
                    setQuotationForm(
                        quotationDetailToFormMapper(data)
                    )
                );

                dispatch(
                    setActiveBranchId(
                        data.branchId
                    )
                );
            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();
            dispatch(setLoading(false));

        }

    };


    /**
 * 🔍 Obtener detalle y navegar
 */
    const findByIdAndNavigate = async (
        quotationId: number
    ) => {

        try {

            showLoadingIndicator();

            dispatch(setLoading(true));

            dispatch(setError(null));

            const response =
                await QuotationService.findById(
                    quotationId
                );

            // console.log(response);

            const {
                statusCode,
                data
            } = response;

            if (statusCode === 200) {

                dispatch(
                    setQuotationForm(
                        quotationDetailToFormMapper(data)
                    )
                );

                dispatch(
                    setActiveBranchId(
                        data.branchId
                    )
                );

                // ==========================
                // 🔹 Navigate
                // ==========================

                navigate('/quotation-register');

            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();

            dispatch(setLoading(false));

        }

    };

    /**
 * 👁️ Obtener detalle para visualización
 */
    const findDetailById = async (
        quotationId: number
    ) => {

        try {

            showLoadingIndicator();

            dispatch(setLoading(true));

            dispatch(setError(null));

            const response =
                await QuotationService.findById(
                    quotationId
                );

            const {
                statusCode,
                data
            } = response;

            if (statusCode === 200) {

                // ==========================
                // 🔹 Save selected quotation
                // ==========================

                dispatch(setSelectedQuotation(data));

                // ==========================
                // 🔹 Open modal
                // ==========================

                dispatch(openQuotationModal());

            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();

            dispatch(setLoading(false));

        }

    };


    /**
 * 📝 Obtener cotizaciones pendientes
 */
    const getPendingQuotations = async () => {

        try {

            showLoadingIndicator();

            dispatch(setLoading(true));

            dispatch(setError(null));

            const response =
                await QuotationService
                    .getPendingQuotations();

            const {
                statusCode,
                data
            } = response;

            if (statusCode === 200) {

                dispatch(
                    setPendingQuotations(data)
                );

            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();

            dispatch(setLoading(false));

        }

    };


    /**
 * 📚 Obtener historial de cotizaciones
 */
    const getQuotationHistory = async () => {

        try {

            showLoadingIndicator();

            dispatch(setLoading(true));

            dispatch(setError(null));

            const response =
                await QuotationService
                    .getQuotationHistory();

            const {
                statusCode,
                data
            } = response;

            if (statusCode === 200) {

                dispatch(
                    setQuotationHistory(data)
                );

            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();

            dispatch(setLoading(false));

        }

    };


    /**
     * 🚀 Inicializar cotización
     */
    const initialize = async (payload: any) => {

        confirm({

            title: '¿Iniciar cotización?',

            message:
                'Se creará un nuevo borrador de cotización.',

            icon: 'info',

            confirmText: 'Continuar',

            cancelText: 'Cancelar',

            onConfirm: async () => {

                const request = initializeQuotationMapper(payload);

                try {


                    showLoadingIndicator();

                    dispatch(setLoading(true));

                    dispatch(setError(null));
                    // dispatch(clearFieldErrors());

                    const response =
                        await QuotationService.initialize(request);

                    // console.log(response);

                    const {
                        statusCode,
                        type,
                        title,
                        message,
                        data
                    } = response;

                    if (statusCode === 200) {

                        dispatch(
                            setQuotationForm(
                                quotationDetailToFormMapper(data)
                            )
                        );

                        dispatch(
                            setActiveBranchId(
                                data.branchId
                            )
                        );

                        dispatch(clearFieldErrors());


                        showAlert(
                            type,
                            title,
                            message
                        );

                    }

                } catch (error: any) {

                    const fieldErrors =
                        error?.response?.data?.data;

                    if (fieldErrors) {

                        dispatch(setFieldErrors(fieldErrors));

                        console.log('Errores de validación:', fieldErrors);
                        return;
                    }

                    // handle(error);

                } finally {

                    hideLoadingIndicator();

                    dispatch(setLoading(false));

                }

            }

        });

    };


    /**
 * 🧾 Actualizar encabezado cotización
 */
    const updateHeader = async (
        quotationId: number,
        payload: any
    ) => {

        confirm({

            title: '¿Actualizar encabezado?',

            message:
                'Se actualizará la información principal de la cotización.',

            icon: 'info',

            confirmText: 'Actualizar',

            cancelText: 'Cancelar',

            onConfirm: async () => {

                const request =
                    initializeQuotationMapper(payload);

                try {

                    showLoadingIndicator();

                    dispatch(setLoading(true));

                    dispatch(setError(null));

                    const response =
                        await QuotationService
                            .updateHeader(
                                quotationId,
                                request
                            );

                    const {
                        statusCode,
                        type,
                        title,
                        message,
                        data
                    } = response;

                    if (statusCode === 200) {

                        dispatch(
                            setQuotationForm(
                                quotationDetailToFormMapper(data)
                            )
                        );

                        dispatch(
                            setActiveBranchId(
                                data.branchId
                            )
                        );

                        showAlert(
                            type,
                            title,
                            message
                        );

                    }

                } catch (error) {

                    handle(error);

                } finally {

                    hideLoadingIndicator();

                    dispatch(setLoading(false));

                }

            }

        });

    };


    const applyDiscount = async (
        quotationId: number,
        discountPercent: number,
        confirmReplaceManualDiscounts: boolean = false,
        target: 'PRODUCTS' | 'TOTAL' = 'PRODUCTS'
    ) => {

        try {

            showLoadingIndicator();

            dispatch(setLoading(true));

            dispatch(setError(null));

            const response =
                await QuotationService
                    .applyDiscount(
                        quotationId,
                        {
                            discountPercent,
                            confirmReplaceManualDiscounts,
                            target
                        }
                    );

            const {
                statusCode,
                type,
                title,
                message,
                data
            } = response;

            if (statusCode === 200) {

                dispatch(
                    setQuotationForm(
                        quotationDetailToFormMapper(data)
                    )
                );

                showAlert(
                    type,
                    title,
                    message
                );

            }

        } catch (error: any) {

            const status =
                error?.response?.data?.statusCode ||
                error?.response?.status;

            // =====================================================
            // 🔹 409: hay descuentos manuales activos — se pide
            // confirmación explícita antes de reemplazarlos.
            // =====================================================

            if (status === 409 && !confirmReplaceManualDiscounts) {

                confirm({

                    title: '¿Aplicar descuento global?',

                    message:
                        error?.response?.data?.message ||
                        'Hay descuentos manuales activos en algunas líneas. Aplicar el descuento global los va a reemplazar.',

                    icon: 'warning',

                    confirmText: 'Sí, reemplazar',

                    cancelText: 'Cancelar',

                    onConfirm: () =>
                        applyDiscount(
                            quotationId,
                            discountPercent,
                            true,
                            target
                        ),

                });

                return;

            }

            handle(error);

        } finally {

            hideLoadingIndicator();

            dispatch(setLoading(false));

        }

    };

    /**
     * 🗑️ Eliminar descuento global de la cotización
     */
    const removeDiscount = async (
        quotationId: number
    ) => {

        try {

            showLoadingIndicator();

            dispatch(setLoading(true));

            dispatch(setError(null));

            const response =
                await QuotationService
                    .removeDiscount(quotationId);

            const {
                statusCode,
                type,
                title,
                message,
                data
            } = response;

            if (statusCode === 200) {

                dispatch(
                    setQuotationForm(
                        quotationDetailToFormMapper(data)
                    )
                );

                showAlert(
                    type,
                    title,
                    message
                );

            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();

            dispatch(setLoading(false));

        }

    };


    /**
     * 📄 Guardar cotización
     */
    const create = async () => {

        confirm({

            title: '¿Guardar cotización?',

            message: 'La cotización será guardada y estará disponible en el historial de cotizaciones.',

            icon: 'info',

            confirmText: 'Guardar',

            cancelText: 'Cancelar',

            onConfirm: async () => {

                try {

                    showLoadingIndicator();
                    dispatch(setLoading(true));
                    dispatch(setError(null));

                    // ==========================
                    // 🔹 Mapper
                    // ==========================

                    const payload = createQuotationMapper(form);



                    // ==========================
                    // 🔹 Request
                    // ==========================

                    const response =
                        await QuotationService.create(
                            form.id,
                            payload
                        );

                    // console.log(response);

                    const {
                        statusCode,
                        type,
                        title,
                        message,
                        data
                    } = response;

                    if (statusCode === 200) {

                        dispatch(setSelectedQuotation(data));

                        // ==========================
                        // 🔹 Clear current quotation
                        // ==========================

                        dispatch(clearQuotationForm());
                        dispatch(clearSelectedProduct());
                        dispatch(setActiveBranchId(null));
                        resetProductPanelState();
                        showAlert(type, title, message);

                    }

                } catch (error) {
                    handle(error);
                } finally {
                    hideLoadingIndicator();
                    dispatch(setLoading(false));
                }

            }

        });

    };

    /**
     * ✏️ Actualizar cotización
     */
    const update = async (quotationId: number, payload: any) => {

        try {

            showLoadingIndicator();
            dispatch(setLoading(true));
            dispatch(setError(null));

            const response = await QuotationService.update(quotationId, payload);

            const { statusCode, type, title, message, data } = response;

            if (statusCode === 200) {

                dispatch(setSelectedQuotation(data));

                showAlert(type, title, message);

            }

        } catch (error) {

            handle(error);

        } finally {

            hideLoadingIndicator();
            dispatch(setLoading(false));

        }

    };


    const processQuotation = async (
        quotationId: number,
        documentTypeId: number
    ) => {

        confirm({

            title: '¿Procesar cotización?',

            message:
                'La cotización será convertida a venta.',

            icon: 'info',

            confirmText: 'Procesar',

            cancelText: 'Cancelar',

            onConfirm: async () => {

                try {

                    showLoadingIndicator();

                    dispatch(setLoading(true));

                    const response =
                        await QuotationService.process(
                            quotationId,
                            {
                                documentTypeId
                            }
                        );

                    // console.log(response);

                    const {
                        statusCode,
                        data,
                        type,
                        title,
                        message
                    } = response;

                    if (statusCode === 200) {

                        continueSale(data);


                        showAlert(
                            type,
                            title,
                            message
                        );

                    }

                } catch (error) {

                    handle(error);

                } finally {

                    hideLoadingIndicator();

                    dispatch(setLoading(false));

                }

            }

        });

    };


    /**
     * ❌ Cancelar cotización
     */
    const cancel = async (
        quotationId: number
    ) => {

        confirm({

            title: '¿Cancelar cotización?',

            message:
                'La cotización será cancelada y no podrá seguir editándose.',

            icon: 'warning',

            confirmText: 'Cancelar cotización',

            cancelText: 'Volver',

            onConfirm: async () => {

                try {

                    showLoadingIndicator();

                    dispatch(setLoading(true));

                    dispatch(setError(null));

                    const response =
                        await QuotationService.cancel(
                            quotationId
                        );

                    const {
                        statusCode,
                        type,
                        title,
                        message,
                        data
                    } = response;

                    if (statusCode === 200) {

                        // ==========================
                        // 🔹 Clear current quotation
                        // ==========================

                        dispatch(clearQuotationForm());
                        dispatch(clearSelectedProduct());
                        dispatch(clearSelectedQuotation());

                        // ==========================
                        // 🔹 Clear cache lists
                        // ==========================

                        dispatch(removePendingQuotationById(data));
                        dispatch(clearQuotations());
                        dispatch(setActiveBranchId(null));
                        resetProductPanelState();

                        showAlert(type, title, message);

                    }

                } catch (error) {

                    handle(error);

                } finally {

                    hideLoadingIndicator();

                    dispatch(setLoading(false));

                }

            }

        });

    };


    /**
 * ❌ Cancelar cotización activa
 */
    const cancelActive = async (
        quotationId: number
    ) => {

        confirm({

            title: '¿Cancelar cotización activa?',

            message:
                'La cotización activa será cancelada mientras no haya sido procesada a venta.',

            icon: 'warning',

            confirmText: 'Cancelar cotización',

            cancelText: 'Volver',

            onConfirm: async () => {

                try {

                    showLoadingIndicator();

                    dispatch(setLoading(true));

                    dispatch(setError(null));

                    const response =
                        await QuotationService
                            .cancelActive(
                                quotationId
                            );

                    // console.log(response);

                    const {
                        statusCode,
                        type,
                        title,
                        message,
                        data
                    } = response;

                    if (statusCode === 200) {

                        dispatch(clearQuotationForm());
                        dispatch(clearSelectedProduct());
                        dispatch(clearSelectedQuotation());

                        dispatch(removeQuotationHistoryById(data));
                        dispatch(clearQuotations());
                        dispatch(setActiveBranchId(null));
                        resetProductPanelState();

                        showAlert(
                            type,
                            title,
                            message
                        );

                    }

                } catch (error) {

                    handle(error);

                } finally {

                    hideLoadingIndicator();

                    dispatch(setLoading(false));

                }

            }

        });

    };


    const handlerResetQuotationFormOnly = () => {

        const hasData =
            form?.items?.length > 0 ||
            form?.customer ||
            form?.seller;

        if (!hasData) {
            return;
        }

        confirm({
            title: '¿Limpiar cotización?',
            message:
                'Se perderán los datos ingresados.\n\n¿Deseas continuar?',
            icon: 'warning',
            confirmText: 'Sí, limpiar',
            cancelText: 'Cancelar',
            onConfirm: () => {

                dispatch(clearQuotationState());

                resetProductPanelState();

            },
        });

    };




    return {

        updateField: <K extends keyof QuotationFormValues>(
            field: K,
            value: QuotationFormValues[K]
        ) =>
            dispatch(
                updateQuotationField({
                    field,
                    value
                })
            ),

        setActiveBranchId: (
            branchId: number | null
        ) => {

            dispatch(
                setActiveBranchId(branchId)
            );

        },

        setForm: (data: QuotationFormValues) => {
            dispatch(setQuotationForm(data));
        },

        clearForm: () => {
            dispatch(clearQuotationForm());
        },


        handlerSelectProduct: (
            product: any | null
        ) => {

            dispatch(
                setSelectedProduct(product)
            );

        },

        handlerSelectQuotation: (
            quotation: any | null
        ) => {

            dispatch(
                setSelectedQuotation(quotation)
            );

        },

        handlerUpdateSelectedProductField: (
            payload: any
        ) => {

            dispatch(
                updateSelectedProductField(payload)
            );

        },

        clearSelectedProduct: () => {

            dispatch(
                clearSelectedProduct()
            );

        },

        activeBranchId,
        form,
        quotations,
        pendingQuotations,
        quotationHistory,
        selectedQuotation,
        selectedProduct,
        loading,
        error,
        fieldErrors,
        filters,

        getPendingQuotations,
        getQuotationHistory,
        search,
        findById,
        findByIdAndNavigate,
        findDetailById,
        initialize,
        updateHeader,
        applyDiscount,
        removeDiscount,
        create,
        update,
        processQuotation,
        cancel,
        cancelActive,

        showQuotationModal,
        showProcessModal,

        handlerResetQuotationFormOnly,

        openQuotationModal: () => { dispatch(openQuotationModal()); },

        closeQuotationModal: () => { dispatch(closeQuotationModal()); },

        openProcessModal: () => {
            dispatch(openProcessModal());
        },

        closeProcessModal: () => {
            dispatch(closeProcessModal());
            dispatch(clearSelectedQuotation());
        },

        clearSelectedQuotation

    };

};
